import type * as rtk from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface Topic {
  _id: { $oid: string };
  topic_name: string;
  project_id: string;
  created_at: string;
  last_updated: string;
  sub_topics: null | string;
  metadata: null | string;
  parent_id: null | string;
  topic_title: string;
}

export interface TopicsState {
  topics: Topic[];
  loading: boolean;
  error: string | null;
}

export interface ServerResponse {
  status: number;
  short_message: string;
  long_message: string;
  data: any;
}

interface FetchRequestParams {
  token: string;
  projectId: string;
  spaceId: string;
  [key: string]: any;
}

const initialState: TopicsState = {
  topics: [],
  loading: false,
  error: null,
};

interface CreateTopicParams {
  token: string;
  name: string;
  projectId: string;
  isParent: boolean;
  topicTitle: string;
  spaceId: string;
}


const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.docloo.co.uk';
  } else {
    return 'http://192.168.53:3011';
  }
};
const backendUrl = getBaseUrl();

// Utility: Handle fetch errors
const handleFetchError = (error: unknown): ServerResponse => {
  if (error instanceof TypeError) {
    return {
      status: 0,
      short_message: 'Network error: Please check your connection.',
      long_message: 'Network error: Unable to fetch data due to connectivity issues.',
      data: null,
    };
  }
  const message = (error as Error).message || 'Unknown error occurred.';
  return { status: 0, short_message: message, long_message: message, data: null };
};

// Fetch topics from the server
export const fetchTopics = createAsyncThunk(
  'topics/fetchTopics',
  async ({ token, projectId, spaceId }: FetchRequestParams, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/topics/get`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ project_id: projectId, space_id: spaceId }),
      });

      if (!response.ok) {
        const errorData: ServerResponse = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to fetch topics.');
      }

      const data = await response.json();
      return data.data as Topic[];
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Create a topic
export const createTopic = createAsyncThunk(
  'topics/createTopic',
  async ({ token, name, projectId, isParent, topicTitle, spaceId }: CreateTopicParams, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/topics/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          project_id: projectId,
          is_parent: isParent,
          topic_title: topicTitle,
          space_id: spaceId,
        }),
      });

      if (!response.ok) {
        const responseData: ServerResponse = await response.json();
        console.log('response', responseData);
        return rejectWithValue(responseData.long_message || 'Failed to create topic.');
      }

      const data = await response.json();
      return data.data as Topic;
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Delete a topic
export const deleteTopic = createAsyncThunk(
  'topics/deleteTopic',
  async ({ token, projectId, topicId }: FetchRequestParams, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/topics/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ project_id: projectId, topic_id: topicId }),
      });

      if (!response.ok) {
        const errorData: ServerResponse = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to delete topic.');
      }

      return { projectId, topicId };
    } catch (error) {
      return rejectWithValue(handleFetchError(error).short_message);
    }
  }
);

// Redux Slice
const topicsSlice = createSlice({
  name: 'topics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopics.fulfilled, (state, action: PayloadAction<Topic[]>) => {
        state.loading = false;
        state.topics = action.payload;
      })
      .addCase(fetchTopics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTopic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTopic.fulfilled, (state, action: PayloadAction<Topic>) => {
        console.log("action.payload", action.payload);
        state.loading = false;
        state.topics.push(action.payload);
      })
      .addCase(createTopic.rejected, (state, action) => {
        state.loading = false;
        state.error = "An error occurred while creating topic.";
      })
      .addCase(deleteTopic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTopic.fulfilled, (state, action: PayloadAction<{ projectId: string; topicId: string }>) => {
        state.loading = false;
        state.topics = state.topics.filter((topic) => topic._id.$oid !== action.payload.topicId);
      })
      .addCase(deleteTopic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default topicsSlice.reducer;
